import React from "react";
import { clearAllWritingDrafts } from "@/lib/writing-draft-storage";
import { User, LogOut, ChevronUp, Settings, MapPin, Bell, RefreshCw, GraduationCap, BookOpen, Presentation, FileText } from "lucide-react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useSidebar } from "@/components/ui/sidebar";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useRole } from "@/contexts/RoleContext";
import { useUserLocation } from "@/hooks/use-user-location";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { LucideIcon } from "lucide-react";

const ROLE_LABELS: Record<string, string> = {
  master_admin: "Master Admin",
  admin: "Associate",
  state_partner: "State Partner",
  growth_partner: 'Growth Partner',
  center_manager: "Centre Manager",
  parent: "Parent",
  tutor: "Tutor",
  student: "Student",
  franchisee_prospect: "Franchisee Prospect",
};

const PROSPECT_ALLOWED_ROUTES = new Set<string>([
  "/learning/courses",
  "/learning/dashboard",
  "/profile",
  "/comms-center",
]);

interface AdminItem {
  title: string;
  url: string;
  icon: LucideIcon;
  allowedRoles: string[];
}

interface SidebarUserMenuProps {
  adminItems: AdminItem[];
}

export function SidebarUserMenu({ adminItems }: SidebarUserMenuProps) {
  const { state } = useSidebar();
  const collapsed = state === "collapsed";
  const navigate = useNavigate();
  const location = useLocation();
  const { userRole, userProfile, allRoles, clearRoleSelection } = useRole();
  const { data: locationName } = useUserLocation();
  const [open, setOpen] = React.useState(false);

  const filteredAdminItems = adminItems.filter(item => {
    if (!userRole || !item.allowedRoles.includes(userRole)) return false;
    // Franchisee prospects only see items in their allowed routes (hide Users, Centres, Communication, Reports, etc.)
    if (userRole === "franchisee_prospect" && !PROSPECT_ALLOWED_ROUTES.has(item.url)) return false;
    return true;
  });

  const handleSignOut = async () => {
    try {
      clearRoleSelection();
      await clearAllWritingDrafts(); // explicit user sign-out only
      await supabase.auth.signOut();
    } catch {
      // Force clear even if signOut fails (e.g. stale/expired session)
      console.warn("SignOut API failed, force-clearing local session");
    }
    // Always clear local auth state and redirect regardless of API result
    for (const key of Object.keys(localStorage)) {
      if (key.startsWith("sb-") || key.startsWith("supabase")) {
        localStorage.removeItem(key);
      }
    }
    toast.success("Signed out successfully");
    navigate("/login");
  };

  const handleSwitchRole = () => {
    clearRoleSelection();
    navigate("/login");
  };

  const isActive = (url: string) => location.pathname.startsWith(url);

  const getInitials = (name: string | null) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button className={`w-full p-3 flex items-center gap-3 rounded-lg hover:bg-muted/50 transition-colors cursor-pointer ${collapsed ? 'justify-center' : ''}`}>
          <Avatar className="h-9 w-9 flex-shrink-0">
            {userProfile?.avatar_url ? (
              <AvatarImage src={userProfile.avatar_url} alt={userProfile?.full_name || 'User'} />
            ) : null}
            <AvatarFallback className="bg-primary/10 text-primary text-sm">
              {getInitials(userProfile?.full_name)}
            </AvatarFallback>
          </Avatar>
          {!collapsed && userProfile && (
            <>
              <div className="flex flex-col overflow-hidden flex-1 text-left">
                <p className="text-sm font-medium text-foreground truncate">
                  {userProfile.full_name || 'User'}
                </p>
                <p className="text-xs text-muted-foreground truncate">
                  {userProfile.email}
                </p>
              </div>
              <ChevronUp className="h-4 w-4 text-muted-foreground" />
            </>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent 
        side={collapsed ? "right" : "top"} 
        align={collapsed ? "start" : "center"}
        className="w-56 z-50"
        sideOffset={8}
      >
        <DropdownMenuLabel className="font-normal">
          <div className="flex flex-col space-y-1">
            <p className="text-sm font-medium">{userProfile?.full_name || 'User'}</p>
            <p className="text-xs text-muted-foreground">{userProfile?.email}</p>
            {userRole && (
              <Badge variant="secondary" className="text-xs w-fit mt-1">
                {ROLE_LABELS[userRole] || userRole}
              </Badge>
            )}
            {locationName && (
              <div className="flex items-center gap-1.5 text-xs mt-1.5">
                <MapPin className="h-3 w-3 flex-shrink-0 text-muted-foreground" />
                <span className="truncate font-medium text-foreground">{locationName}</span>
              </div>
            )}
          </div>
        </DropdownMenuLabel>
        
        {filteredAdminItems.length > 0 && (
          <>
            <DropdownMenuSeparator />
            {filteredAdminItems.map((item) => {
              const Icon = item.icon;
              return (
                <DropdownMenuItem 
                  key={item.url} 
                  asChild
                  className={isActive(item.url) ? "bg-muted" : ""}
                >
                  <Link
                    to={item.url}
                    onClick={(e) => {
                      if (userRole === "franchisee_prospect" && !PROSPECT_ALLOWED_ROUTES.has(item.url)) {
                        e.preventDefault();
                        toast.info("Contact your administrator for access to this section.");
                      }
                      setOpen(false);
                    }}
                    className="flex items-center gap-2 cursor-pointer"
                  >
                    <Icon className="h-4 w-4" />
                    <span>{item.title}</span>
                  </Link>
                </DropdownMenuItem>
              );
            })}
          </>
        )}
        
        {/* Comms Center - For all users */}
        <DropdownMenuSeparator />
        <DropdownMenuItem 
          asChild 
          className={isActive('/comms-center') ? "bg-muted" : ""}
        >
          <Link 
            to="/comms-center" 
            onClick={() => setOpen(false)}
            className="flex items-center gap-2 cursor-pointer"
          >
            <Bell className="h-4 w-4" />
            <span>Comms Center</span>
          </Link>
        </DropdownMenuItem>

        {/* Understanding Lurneax - Staff only */}
        {userRole && ['master_admin', 'admin', 'state_partner', 'center_manager', 'tutor'].includes(userRole) && (
          <DropdownMenuItem 
            className={`flex items-center gap-2 cursor-pointer ${isActive('/understanding-lurneax') ? "bg-muted" : ""}`}
            onSelect={() => {
              setOpen(false);
              navigate("/understanding-lurneax");
            }}
          >
            <BookOpen className="h-4 w-4" />
            <span>Understanding Lurneax</span>
          </DropdownMenuItem>
        )}

        {/* Running EOT Tests Guide - Staff only */}
        {userRole && ['master_admin', 'admin', 'state_partner', 'center_manager', 'tutor'].includes(userRole) && (
          <DropdownMenuItem 
            className={`flex items-center gap-2 cursor-pointer ${isActive('/running-eot-guide') ? "bg-muted" : ""}`}
            onSelect={() => {
              setOpen(false);
              navigate("/running-eot-guide");
            }}
          >
            <BookOpen className="h-4 w-4" />
            <span>Running EOT Tests</span>
          </DropdownMenuItem>
        )}

        {/* Running Exams Guide - Staff only */}
        {userRole && ['master_admin', 'admin', 'state_partner', 'center_manager', 'tutor'].includes(userRole) && (
          <DropdownMenuItem
            className={`flex items-center gap-2 cursor-pointer ${isActive('/running-exam-guide') ? "bg-muted" : ""}`}
            onSelect={() => {
              setOpen(false);
              navigate("/running-exam-guide");
            }}
          >
            <BookOpen className="h-4 w-4" />
            <span>Running Exams</span>
          </DropdownMenuItem>
        )}

        {/* Homework Videos & Handouts Guide - Staff only */}
        {userRole && ['master_admin', 'admin', 'state_partner', 'center_manager', 'tutor'].includes(userRole) && (
          <DropdownMenuItem 
            className={`flex items-center gap-2 cursor-pointer ${isActive('/homework-guide') ? "bg-muted" : ""}`}
            onSelect={() => {
              setOpen(false);
              navigate("/homework-guide");
            }}
          >
            <BookOpen className="h-4 w-4" />
            <span>Homework Videos & Handouts</span>
          </DropdownMenuItem>
        )}

        {/* Schoolola Strategy - Master Admin & Admin only */}
        {userRole && ['master_admin', 'admin'].includes(userRole) && (
          <DropdownMenuItem 
            className={`flex items-center gap-2 cursor-pointer ${isActive('/schoolola-strategy') ? "bg-muted" : ""}`}
            onSelect={() => {
              setOpen(false);
              navigate("/schoolola-strategy");
            }}
          >
            <Presentation className="h-4 w-4" />
            <span>Schoolola Strategy</span>
          </DropdownMenuItem>
        )}

        {/* Downloadable Resources - All authenticated users */}
        <DropdownMenuItem
          className={`flex items-center gap-2 cursor-pointer ${isActive('/resources/downloads') ? "bg-muted" : ""}`}
          onSelect={() => {
            setOpen(false);
            navigate("/resources/downloads");
          }}
        >
          <FileText className="h-4 w-4" />
          <span>Downloadable Resources</span>
        </DropdownMenuItem>

        {/* Profile Link - For all users */}
        <DropdownMenuItem 
          asChild 
          className={isActive('/profile') || isActive('/parent/profile') ? "bg-muted" : ""}
        >
          <Link 
            to={userRole === 'parent' ? '/parent/profile' : '/profile'} 
            onClick={() => setOpen(false)}
            className="flex items-center gap-2 cursor-pointer"
          >
            <Settings className="h-4 w-4" />
            <span>My Account</span>
          </Link>
        </DropdownMenuItem>

        {/* Tutor Profile Link - For tutors */}
        {userRole === 'tutor' && (
          <DropdownMenuItem 
            asChild 
            className={isActive('/tutor-profile') ? "bg-muted" : ""}
          >
            <Link 
              to="/tutor-profile" 
              onClick={() => setOpen(false)}
              className="flex items-center gap-2 cursor-pointer"
            >
              <GraduationCap className="h-4 w-4" />
              <span>Tutor Profile</span>
            </Link>
          </DropdownMenuItem>
        )}

        {/* Tutor Network Link - For admin roles */}
        {(userRole === 'master_admin' || userRole === 'admin' || userRole === 'state_partner') && (
          <DropdownMenuItem 
            asChild 
            className={isActive('/tutor-network') ? "bg-muted" : ""}
          >
            <Link 
              to="/tutor-network" 
              onClick={() => setOpen(false)}
              className="flex items-center gap-2 cursor-pointer"
            >
              <GraduationCap className="h-4 w-4" />
              <span>Tutor Profiles</span>
            </Link>
          </DropdownMenuItem>
        )}

        <DropdownMenuSeparator />
        {allRoles.length > 1 && (
          <DropdownMenuItem onClick={handleSwitchRole} className="cursor-pointer">
            <RefreshCw className="h-4 w-4 mr-2" />
            Switch Role
          </DropdownMenuItem>
        )}
        <DropdownMenuItem onClick={handleSignOut} className="text-destructive focus:text-destructive cursor-pointer">
          <LogOut className="h-4 w-4 mr-2" />
          Sign Out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
